import PropTypes from 'prop-types';
import { useState } from 'react';
// @mui
import {
  Box,
  Button,
  Collapse,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
// components
import Iconify from 'src/components/Iconify';

// ----------------------------------------------------------------------

TaskFilterToolbar.propTypes = {
  filterQuery: PropTypes.string,
  onFilterName: PropTypes.func,
  onResetFilters: PropTypes.func,
  statusId: PropTypes.string,
  setStatusId: PropTypes.func,
  userId: PropTypes.string,
  setUserId: PropTypes.func,
  projectId: PropTypes.string,
  setProjectId: PropTypes.func,
  startDate: PropTypes.string,
  setStartDate: PropTypes.func,
  endDate: PropTypes.string,
  setEndDate: PropTypes.func,
  sortBy: PropTypes.string,
  setSortBy: PropTypes.func,
  sortDirection: PropTypes.string,
  setSortDirection: PropTypes.func,
  projectOptions: PropTypes.array,
  userOptions: PropTypes.array,
  statusOptions: PropTypes.array,
};

export default function TaskFilterToolbar({
  filterQuery,
  onFilterName,
  onResetFilters,
  statusId,
  setStatusId,
  userId,
  setUserId,
  projectId,
  setProjectId,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  sortBy,
  setSortBy,
  sortDirection,
  setSortDirection,
  projectOptions = [],
  userOptions = [],
  statusOptions = [],
}) {
  const [showFilters, setShowFilters] = useState(false);

  const handleSortDirectionChange = () => {
    setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
  };

  return (
    <Stack spacing={2} sx={{ px: 2.5, py: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <TextField
          fullWidth
          value={filterQuery}
          onChange={(e) => onFilterName(e.target.value)}
          placeholder="Search task by title or ticket ID..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        <Button
          variant="outlined"
          color="inherit"
          startIcon={<Iconify icon="eva:funnel-fill" />}
          onClick={() => setShowFilters(!showFilters)}
        >
          Filters
        </Button>
      </Stack>

      <Collapse in={showFilters}>
        <Box sx={{ pt: 2, pb: 1 }}>
          <Typography variant="subtitle1" gutterBottom>
            Advanced Filters
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Project</InputLabel>
                <Select label="Project" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                  <MenuItem value="">All Projects</MenuItem>
                  {projectOptions.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={statusId} onChange={(e) => setStatusId(e.target.value)}>
                  <MenuItem value="">All Statuses</MenuItem>
                  {statusOptions.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      {status.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Assigned User</InputLabel>
                <Select label="Assigned User" value={userId} onChange={(e) => setUserId(e.target.value)}>
                  <MenuItem value="">All Users</MenuItem>
                  {userOptions.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.first_name} {user.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="End Date"
                type="date"
                fullWidth
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Sort By</InputLabel>
                <Select label="Sort By" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <MenuItem value="due_date">Due Date</MenuItem>
                  <MenuItem value="title">Title</MenuItem>
                  <MenuItem value="priority">Priority</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <Tooltip title={sortDirection === 'ASC' ? 'Ascending' : 'Descending'}>
                <IconButton
                  color="primary"
                  onClick={handleSortDirectionChange}
                  sx={{ border: '1px solid', borderColor: 'divider', height: 40, width: '40' }}
                >
                  <Iconify icon={sortDirection === 'ASC' ? 'eva:arrow-up-fill' : 'eva:arrow-down-fill'} width={24} />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              color="inherit"
              variant="outlined"
              startIcon={<Iconify icon="eva:refresh-fill" />}
              onClick={onResetFilters}
            >
              Reset
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Stack>
  );
}
